import { FieldWrapper, TextField } from '@td-client-frontend/common';
import { ChevronDown, Search } from '@td-client-frontend/icons';
import clsx from 'clsx';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Permission } from '../../../@types/Roles';

import { FeatureCheckbox } from './FeatureCheckbox';

export enum FeatureField {
  Read = 'read',
  Write = 'write',
  Create = 'create'
}

interface FeaturesGridProps {
  data: Permission[];
  onChange: (value: any) => void;
  readOnly: boolean;
}

export const FeaturesGrid = ({ data, onChange, readOnly }: FeaturesGridProps) => {
  const [sortAsc, setSortAsc] = useState(true);
  const [featuresList, setFeaturesList] = useState<Permission[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const filterData = (featuresData: Permission[]) =>
      featuresData.filter((feature) => {
        return feature.name ? new RegExp(search, 'i').test(feature.name) : feature;
      });

    const sortData = (featuresData: Permission[]) => {
      const sortedData = _.sortBy(featuresData, 'name');

      return (sortAsc && sortedData) || sortedData.reverse();
    };

    data?.length && setFeaturesList(sortData(filterData(data)));
  }, [data, sortAsc, search]);

  const handleSearch = (searchStr: string) => {
    if (searchStr && searchStr.length > 2) {
      setSearch(searchStr);
    } else {
      if (search) {
        setSearch('');
      }
    }
  };

  const handleSortChange = () => {
    setSortAsc(!sortAsc);
  };

  const updateFeature = (feature: Permission, field: FeatureField, value: boolean) => {
    feature[field] = value;
    onChange(feature);
  };

  const updateFeatureWithField = (field: FeatureField, feature: Permission) => (value: boolean) =>
    updateFeature(feature, field, value);

  return (
    <>
      <div className="flex w-full sm:w-1/2">
        <FieldWrapper label="Search" name="search" error="">
          <TextField
            name="search"
            id="featureSearch"
            placeholder="Search for a field or a feature"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
            Right={
              <div className="pr-5">
                <Search data-testid="search-button" onClick={() => handleSearch(search)} />
              </div>
            }
          />
        </FieldWrapper>
      </div>
      <div>
        <table className="w-full table-auto" data-testid="permission-table">
          <thead>
            <tr className="text-center uppercase border-b-2 border-primary-500 text-gray-30">
              <th className="flex flex-row items-center pl-10">
                FIELD/FEATURE NAME{' '}
                <button
                  type="button"
                  onClick={handleSortChange}
                  data-testid="sorting-button"
                  className={clsx(
                    sortAsc ? '' : 'rotate-180',
                    'mx-2 focus:outline-none duration-75 transform transition'
                  )}
                >
                  <ChevronDown />
                </button>
              </th>
              <th>READ</th>
              <th>EDIT</th>
              <th>CREATE</th>
            </tr>
          </thead>
          <tbody>
            {featuresList?.map((feature: Permission, idx) => {
              return (
                <tr key={`feature-key-${idx}-${feature.name}`} className="border-b-2 border-gray-30 odd:bg-gray-15">
                  <td className="pl-10">
                    <div>{feature.displayName || ''}</div>
                  </td>
                  <td>
                    <div className="flex flex-row justify-center">
                      <FeatureCheckbox
                        checked={feature.read}
                        onChange={updateFeatureWithField(FeatureField.Read, feature)}
                        disabled={readOnly}
                        id={`${feature.name}-${FeatureField.Read}-${idx}`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row justify-center">
                      <FeatureCheckbox
                        checked={feature.write}
                        onChange={updateFeatureWithField(FeatureField.Write, feature)}
                        disabled={readOnly}
                        id={`${feature.name}-${FeatureField.Write}-${idx}`}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="flex flex-row justify-center">
                      <FeatureCheckbox
                        checked={feature.create}
                        onChange={updateFeatureWithField(FeatureField.Create, feature)}
                        disabled={readOnly}
                        id={`${feature.name}-${FeatureField.Create}-${idx}`}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};
